import React, { useEffect, useState } from "react";
import {
    adminListCaseStudies,
    adminCreateCaseStudy,
    adminUpdateCaseStudy,
    adminDeleteCaseStudy,
    uploadImage,
} from "@/lib/api";

const CATEGORIES = [
    "Marketing Campaign",
    "YouTube Design",
    "Social Media",
    "Community Management",
    "Graphic Design",
    "Brand System",
];

const empty = {
    title: "",
    category: CATEGORIES[0],
    client: "",
    year: String(new Date().getFullYear()),
    slug: "",
    description: "",
    cover_url: "",
    cover_key: "",
    gallery: [],
    external_url: "",
    published: true,
    order: 0,
};

export default function CaseStudiesPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // full doc or {new: true, ...empty}
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const formatError = (e) => {
        const d = e?.response?.data?.detail;
        if (Array.isArray(d)) {
            return d
                .map((x) => {
                    const field = Array.isArray(x?.loc)
                        ? x.loc.slice(1).join(".") || x.loc.join(".")
                        : "field";
                    return `${field}: ${x?.msg || "invalid"}`;
                })
                .join(" · ");
        }
        if (typeof d === "string") return d;
        if (d && typeof d === "object") return JSON.stringify(d);
        return e?.message || "Unknown error";
    };

    const load = async () => {
        setLoading(true);
        try {
            setItems(await adminListCaseStudies());
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setSaveError("");
        setEditing({ new: true, ...empty });
    };
    const openEdit = (row) => {
        setSaveError("");
        setEditing({ ...row });
    };
    const close = () => {
        setSaveError("");
        setEditing(null);
    };

    const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

    const uploadCover = async (file) => {
        setUploading(true);
        setSaveError("");
        try {
            const { url, key } = await uploadImage(file, "covers");
            setEditing((e) => ({
                ...e,
                cover_url: url,
                cover_key: key,
            }));
        } catch (e) {
            setSaveError("Cover upload failed: " + formatError(e));
        } finally {
            setUploading(false);
        }
    };

    const uploadGallery = async (files) => {
        setUploading(true);
        setSaveError("");
        try {
            const results = [];
            for (const file of Array.from(files)) {
                const r = await uploadImage(file, "gallery");
                results.push(r);
            }
            setEditing((e) => ({
                ...e,
                gallery: [...(e.gallery || []), ...results],
            }));
        } catch (e) {
            setSaveError("Gallery upload failed: " + formatError(e));
        } finally {
            setUploading(false);
        }
    };

    const removeGalleryItem = (idx) =>
        setEditing((e) => ({
            ...e,
            gallery: e.gallery.filter((_, i) => i !== idx),
        }));

    const save = async () => {
        setSaveError("");
        if (!editing.title?.trim()) {
            setSaveError("Title is required.");
            return;
        }
        if (!editing.category?.trim()) {
            setSaveError("Category is required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: editing.title,
                category: editing.category,
                client: editing.client,
                year: editing.year,
                slug: editing.slug || "",
                description: editing.description,
                cover_url: editing.cover_url,
                cover_key: editing.cover_key,
                gallery: editing.gallery,
                external_url: editing.external_url,
                published: editing.published,
                order: Number(editing.order) || 0,
            };
            if (editing.new) {
                await adminCreateCaseStudy(payload);
            } else {
                await adminUpdateCaseStudy(editing.id, payload);
            }
            close();
            await load();
        } catch (e) {
            setSaveError("Save failed — " + formatError(e));
        } finally {
            setSaving(false);
        }
    };

    const del = async (row) => {
        if (!window.confirm(`Delete "${row.title}"? This will also delete uploaded images.`))
            return;
        await adminDeleteCaseStudy(row.id);
        await load();
    };

    return (
        <section data-testid="admin-case-studies">
            <header className="flex items-end justify-between border-b border-[#E6E2D6] pb-6 mb-8">
                <div>
                    <p className="eyebrow text-[#1B1B1B]/60 mb-2">
                        § Work — 01
                    </p>
                    <h1 className="font-serif-editorial text-4xl md:text-5xl leading-[1.02] tracking-[-0.02em]">
                        Case Studies
                    </h1>
                </div>
                <button
                    type="button"
                    onClick={openNew}
                    data-testid="cs-new"
                    className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500"
                >
                    + New Case Study
                </button>
            </header>

            {loading ? (
                <p className="eyebrow text-[#1B1B1B]/50">Loading…</p>
            ) : items.length === 0 ? (
                <p className="font-serif-editorial italic text-2xl text-[#1B1B1B]/70">
                    No case studies yet. Create the first one.
                </p>
            ) : (
                <div className="border-t border-[#E6E2D6]">
                    {items.map((row) => (
                        <div
                            key={row.id}
                            data-testid={`cs-row-${row.id}`}
                            className="grid grid-cols-12 gap-4 items-center py-4 border-b border-[#E6E2D6]"
                        >
                            <div className="col-span-2">
                                {row.cover_url ? (
                                    <div className="w-full aspect-[4/5] bg-[#1B1B1B] overflow-hidden">
                                        <img
                                            src={row.cover_url}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="w-full aspect-[4/5] bg-[#EFEBDF] flex items-center justify-center">
                                        <span className="eyebrow text-[#1B1B1B]/50">
                                            No cover
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="col-span-5">
                                <p className="eyebrow text-[#1B1B1B]/60 mb-1">
                                    {row.category} · {row.year || "—"}
                                </p>
                                <h3 className="font-serif-editorial text-2xl leading-[1.1]">
                                    {row.title}
                                </h3>
                                {row.client && (
                                    <p className="font-sans-tight text-xs text-[#1B1B1B]/60 mt-1">
                                        Client · {row.client}
                                    </p>
                                )}
                            </div>
                            <div className="col-span-2">
                                <p className="eyebrow text-[#1B1B1B]/60">
                                    {row.published ? "Published" : "Draft"}
                                </p>
                                <p className="font-sans-tight text-xs text-[#1B1B1B]/60">
                                    Order · {row.order ?? 0}
                                </p>
                            </div>
                            <div className="col-span-3 flex gap-6 justify-end">
                                <button
                                    type="button"
                                    onClick={() => openEdit(row)}
                                    data-testid={`cs-edit-${row.id}`}
                                    className="eyebrow link-underline"
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => del(row)}
                                    data-testid={`cs-delete-${row.id}`}
                                    className="eyebrow link-underline text-red-800"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Editor drawer */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-[#1B1B1B]/40 flex justify-end">
                    <div className="w-full max-w-3xl h-full overflow-y-auto bg-[#F6F3EC] p-8 md:p-12">
                        <div className="flex items-center justify-between mb-8">
                            <p className="eyebrow text-[#1B1B1B]/60">
                                {editing.new ? "New Case Study" : "Edit Case Study"}
                            </p>
                            <button
                                type="button"
                                onClick={close}
                                className="eyebrow link-underline"
                            >
                                Close ✕
                            </button>
                        </div>

                        <div className="space-y-6">
                            <Field label="Title">
                                <input
                                    className="editorial-input"
                                    value={editing.title}
                                    onChange={(e) => setField("title", e.target.value)}
                                    data-testid="cs-title"
                                />
                            </Field>
                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Category">
                                    <select
                                        className="editorial-input"
                                        value={editing.category}
                                        onChange={(e) => setField("category", e.target.value)}
                                        data-testid="cs-category"
                                    >
                                        {CATEGORIES.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Year">
                                    <input
                                        className="editorial-input"
                                        value={editing.year}
                                        onChange={(e) => setField("year", e.target.value)}
                                        placeholder="2026"
                                    />
                                </Field>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Client">
                                    <input
                                        className="editorial-input"
                                        value={editing.client}
                                        onChange={(e) => setField("client", e.target.value)}
                                        placeholder="Accredian"
                                    />
                                </Field>
                                <Field label="External URL">
                                    <input
                                        className="editorial-input"
                                        value={editing.external_url}
                                        onChange={(e) =>
                                            setField("external_url", e.target.value)
                                        }
                                        placeholder="https://…"
                                    />
                                </Field>
                            </div>

                            <Field label="Description">
                                <textarea
                                    rows={5}
                                    className="editorial-input resize-none"
                                    value={editing.description}
                                    onChange={(e) => setField("description", e.target.value)}
                                    data-testid="cs-description"
                                />
                            </Field>

                            <Field
                                label="Cover Image"
                                hint="Upload at 1600 × 2000 px (4:5, portrait) and nothing is cropped. The Work grid is a set of equal 4:5 tiles, so any other shape is trimmed to fit — a wide photo loses its sides. JPG, PNG, WebP or AVIF, up to 15 MB. This is the listing image only; it is not shown inside the case study."
                            >
                                <div className="flex items-center gap-6">
                                    {/* The preview is deliberately the same 4:5
                                        the site uses, so what gets cropped is
                                        visible here rather than discovered
                                        after publishing. */}
                                    {editing.cover_url ? (
                                        <div className="w-32 aspect-[4/5] bg-[#1B1B1B] overflow-hidden">
                                            <img
                                                src={editing.cover_url}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-32 aspect-[4/5] bg-[#EFEBDF] flex items-center justify-center">
                                            <span className="eyebrow text-[#1B1B1B]/50 text-center px-2">
                                                No cover
                                            </span>
                                        </div>
                                    )}
                                    <label className="inline-flex items-center gap-2 cursor-pointer eyebrow border border-[#1B1B1B] px-5 py-3 hover:bg-[#1B1B1B] hover:text-[#F6F3EC] transition-colors duration-300">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) =>
                                                e.target.files?.[0] && uploadCover(e.target.files[0])
                                            }
                                            data-testid="cs-cover-upload"
                                        />
                                        {uploading ? "Uploading…" : "Upload"}
                                    </label>
                                </div>
                            </Field>

                            <Field
                                label="Gallery"
                                hint="Any shape, any size — these are shown whole, never cropped, at the resolution you upload. JPG, PNG, WebP or AVIF, up to 15 MB each. The gallery is the case study: a project with no gallery images has no imagery on its page, since the cover is not repeated there."
                            >
                                <div className="flex flex-wrap gap-3 mb-4">
                                    {(editing.gallery || []).map((g, i) => (
                                        <div key={i} className="relative">
                                            <img
                                                src={g.url}
                                                alt=""
                                                className="w-24 h-24 object-cover bg-[#1B1B1B]"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeGalleryItem(i)}
                                                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#1B1B1B] text-[#F6F3EC] text-xs"
                                                aria-label="Remove"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <label className="inline-flex items-center gap-2 cursor-pointer eyebrow border border-[#1B1B1B] px-5 py-3 hover:bg-[#1B1B1B] hover:text-[#F6F3EC] transition-colors duration-300">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) =>
                                            e.target.files?.length && uploadGallery(e.target.files)
                                        }
                                        data-testid="cs-gallery-upload"
                                    />
                                    {uploading ? "Uploading…" : "Add images"}
                                </label>
                            </Field>

                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Published">
                                    <select
                                        className="editorial-input"
                                        value={editing.published ? "yes" : "no"}
                                        onChange={(e) =>
                                            setField("published", e.target.value === "yes")
                                        }
                                    >
                                        <option value="yes">Yes — live on site</option>
                                        <option value="no">No — draft</option>
                                    </select>
                                </Field>
                                <Field label="Order">
                                    <input
                                        type="number"
                                        className="editorial-input"
                                        value={editing.order ?? 0}
                                        onChange={(e) => setField("order", e.target.value)}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="mt-10 pt-6 border-t border-[#E6E2D6] flex flex-col gap-4">
                            {saveError && (
                                <div
                                    data-testid="cs-save-error"
                                    className="border border-red-800/40 bg-red-800/5 text-red-800 font-sans-tight text-sm p-4"
                                >
                                    {saveError}
                                </div>
                            )}
                            <div className="flex justify-end gap-4">
                                <button
                                    type="button"
                                    onClick={close}
                                    className="eyebrow link-underline"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={saving}
                                    data-testid="cs-save"
                                    className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500 disabled:opacity-60"
                                >
                                    {saving ? "Saving…" : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

const Field = ({ label, hint, children }) => (
    <div>
        <label className="eyebrow text-[#1B1B1B]/60">{label}</label>
        {children}
        {hint && (
            <p className="mt-2 font-sans-tight text-xs text-[#1B1B1B]/55 leading-[1.5] max-w-md font-light">
                {hint}
            </p>
        )}
    </div>
);
